import { BillingOrderDetail } from '@/services/billing/types/billing-order-detail.type';
import { OrganizationInfoCard } from '@components/data-display/organization';
import { Card, CardContent, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BillingDistributionTabs } from '../components/table/billing-distribution-tab';
import { BillingItemsTable } from '../components/table/billing-items-table';
import {
  transformMilestonesToGroupedItems,
  transformSalesOrdersToDistributionList,
} from '../utils/billing-information.utils';

interface BillingOrderInformationViewProps {
  orderData: BillingOrderDetail | undefined;
  isLoading: boolean;
}

export const BillingOrderInformationView = ({
  orderData,
  isLoading,
}: BillingOrderInformationViewProps) => {
  const { t } = useTranslation();

  // Transform milestones to grouped items for BillingItemsTable
  const groupedItems = useMemo(() => {
    if (!orderData?.milestones || !orderData?.salesOrders) return [];
    return transformMilestonesToGroupedItems(orderData.milestones, orderData.salesOrders);
  }, [orderData?.milestones, orderData?.salesOrders]);

  // Transform salesOrders to distribution list for BillingDistributionTabs
  const distributionList = useMemo(() => {
    if (!orderData?.salesOrders || !orderData?.milestones) return [];
    return transformSalesOrdersToDistributionList(orderData.salesOrders, orderData.milestones);
  }, [orderData?.salesOrders, orderData?.milestones]);

  // Transform organization data for OrganizationInfoCard
  const organizationData = useMemo(() => {
    if (!orderData?.organization) return null;
    return {
      _id: orderData.organization._id,
      name: orderData.organization.name,
      country: orderData.organization.country,
      owner: orderData.organization.owner,
      segment: orderData.organization.segment,
    };
  }, [orderData?.organization]);

  const currentOrder = useMemo(() => {
    if (!orderData) return null;
    return {
      status: orderData.status,
      number: orderData.number,
      orderType: orderData.orderType,
    };
  }, [orderData]);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography>{t('Loading...')}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!orderData) {
    return (
      <Card>
        <CardContent>
          <Typography>{t('Billing order not found')}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <OrganizationInfoCard
        organizationData={organizationData}
        contacts={[]}
        currentOrder={currentOrder}
      />
      <BillingItemsTable items={groupedItems} />
      <BillingDistributionTabs distributionListItem={distributionList} />
    </>
  );
};
